import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { Stack } from "@fluentui/react/lib/Stack";
import { Link } from "@fluentui/react/lib/Link";

import { WithTranslator } from "Components/Generic/Translator";
import { hasNoElements } from "Utils/Array";
import { sortItems } from "Utils/Sort";
import { IContact } from "Model/Storage/IContact";
import { getName } from "Utils/Item";
import { uiSetActiveCompanyId, uiSetActiveContactId } from "Store/Actions";
import { ICompany } from "Model/Storage/ICompany";
import { ICompanyContactsProps } from "./ICompanyContactsProps";
import { ContactIcon } from "./ContactIcon";
import { useCompanyContactsStyles } from "./UseCompanyContactsStyles";

export const CompanyContacts = WithTranslator((props: ICompanyContactsProps): JSX.Element => {
  const { company, translator } = props;
  const { contacts } = company as ICompany;
  const styles = useCompanyContactsStyles();
  const dispatch = useDispatch();
  const onContactClick = useCallback(
    (event: React.MouseEvent<HTMLElement | HTMLAnchorElement | HTMLButtonElement, MouseEvent>) => {
      const contactId = (event?.target as HTMLElement)?.id;
      dispatch(uiSetActiveCompanyId(company[`@id`]));
      dispatch(uiSetActiveContactId(contactId));
    },
    [company, dispatch]
  );

  if (hasNoElements(contacts)) {
    return null;
  }

  const sortedContacts: IContact[] = sortItems(contacts, [[`@isRelevant`, true]], translator.getActiveLocale());
  return (
    <Stack>
      {sortedContacts.slice(0, 3).map((contact) => (
        <Stack key={contact[`@id`]} className={styles.root} horizontal tokens={{ childrenGap: 5 }}>
          <Stack.Item>
            <ContactIcon contact={contact} />
          </Stack.Item>
          <Stack.Item className={styles.name}>
            <Link id={contact[`@id`]} onClick={onContactClick}>
              {getName(contact)}
            </Link>
          </Stack.Item>
        </Stack>
      ))}
      {sortedContacts.length > 3
        ? (
          <Stack.Item className={styles.more}>
            {`+${contacts.length - 3} ${translator.get(`details.more`, `more`)}`}
          </Stack.Item>
        )
        : null}
    </Stack>
  );
});
